import { Suspense } from 'react';
import { BulkStoreAssignments, BulkStoreAssignmentsSkeleton, EventsTab, MediaFormatsList, MediaFormatsListSkeleton, ReportingTab } from '@/components/retailer';
import { BookingCyclesList } from '@/components/retailer/BookingCyclesList';
import { RetailerPacksTab } from '@/components/retailer/RetailerPacksTab';
import { RetailerStandardsTab } from '@/components/retailer/RetailerStandardsTab';
import { StoresList } from '@/components/retailer/StoresList';
import RetailerInstoreLocationsPage from '@/pages/admin/retailers/[retailerId]/in-store-locations';
import RetailerLabelsPage from '@/pages/admin/retailers/[retailerId]/labels';
import RetailerStoreFormatsPage from '@/pages/admin/retailers/[retailerId]/store-formats';
import { OverviewSection } from './OverviewSection';
import { useRetailerWorkspace } from './RetailerWorkspaceContext';
import type { RetailerSectionId } from './sections';

interface SectionContentProps {
  section: RetailerSectionId;
  onEditRetailer: () => void;
  onArchiveRetailer: () => void;
}

export function SectionContent({
  section,
  onEditRetailer,
  onArchiveRetailer,
}: SectionContentProps) {
  const { activeRetailer } = useRetailerWorkspace();

  if (!activeRetailer) {
    return null;
  }

  switch (section) {
    case 'overview':
      return (
        <OverviewSection
          onEditRetailer={onEditRetailer}
          onArchiveRetailer={onArchiveRetailer}
        />
      );
    case 'stores':
      return <StoresList retailerId={activeRetailer.id} />;
    case 'formats':
      return <RetailerStoreFormatsPage retailerIdOverride={activeRetailer.id} />;
    case 'locations':
      return <RetailerInstoreLocationsPage retailerIdOverride={activeRetailer.id} />;
    case 'labels':
      return <RetailerLabelsPage retailerIdOverride={activeRetailer.id} />;
    case 'media':
      return (
        <Suspense fallback={<MediaFormatsListSkeleton />}>
          <MediaFormatsList retailerId={activeRetailer.id} retailerName={activeRetailer.name} />
        </Suspense>
      );
    case 'packs':
      return <RetailerPacksTab retailerId={activeRetailer.id} />;
    case 'assignments':
      return (
        <Suspense fallback={<BulkStoreAssignmentsSkeleton />}>
          <BulkStoreAssignments retailerId={activeRetailer.id} retailerName={activeRetailer.name} />
        </Suspense>
      );
    case 'standards':
      return (
        <RetailerStandardsTab
          retailerId={activeRetailer.id}
          retailerName={activeRetailer.name}
        />
      );
    case 'cycles':
      return <BookingCyclesList retailerId={activeRetailer.id} retailer={activeRetailer} />;
    case 'events':
      return <EventsTab retailerId={activeRetailer.id} />;
    case 'reporting':
      return (
        <ReportingTab retailerId={activeRetailer.id} retailerName={activeRetailer.name} />
      );
    default: {
      const _exhaustive: never = section;
      return _exhaustive;
    }
  }
}
